import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarCheck, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { SectionHead } from '../SectionHead';
import type { AbsenceListDto } from '../../../types/dashboard';

interface ConflictItem {
  id: string;
  employeeName: string;
  date: string;
  message: string;
}

interface Props {
  conflicts: ConflictItem[];
  approvals: AbsenceListDto[];
}

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export function AttentionCard({ conflicts, approvals }: Props) {
  const { t } = useTranslation('dashboard');
  const isEmpty = conflicts.length === 0 && approvals.length === 0;

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead
        kicker={t('admin.attention.kicker')}
        title={t('admin.attention.title')}
        action={
          <span
            className={`font-mono-num text-xs px-2 py-0.5 rounded-full ${
              isEmpty
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {conflicts.length + approvals.length}
          </span>
        }
      />

      {isEmpty ? (
        <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/10 py-6 text-sm text-emerald-700 dark:text-emerald-200">
          <CheckCircle2 className="w-5 h-5" aria-hidden />
          {t('admin.attention.empty')}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {conflicts.length > 0 && (
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.16em] text-ink-faint mb-2">
                {t('admin.attention.conflictsTitle')}
              </div>
              <div className="space-y-2">
                {conflicts.map((c) => (
                  <Link
                    key={c.id}
                    to="/planning"
                    className="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-900/30 bg-red-50/60 dark:bg-red-900/10 p-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <AlertTriangle
                      className="w-4 h-4 text-status-conflict mt-0.5 shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink dark:text-slate-50 truncate">
                        {c.employeeName} · {formatDate(c.date)}
                      </div>
                      <div className="text-xs text-ink-muted dark:text-slate-400 truncate">
                        {c.message}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {approvals.length > 0 && (
            <div>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.16em] text-ink-faint mb-2">
                {t('admin.attention.approvalsTitle')}
              </div>
              <div className="space-y-2">
                {approvals.map((a) => (
                  <Link
                    key={a.absenceId}
                    to="/absences"
                    className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-900/30 bg-amber-50/60 dark:bg-amber-900/10 p-3 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                  >
                    <CalendarCheck
                      className="w-4 h-4 text-amber-600 dark:text-amber-300 mt-0.5 shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink dark:text-slate-50 truncate">
                        {a.employeeName ?? '—'}
                      </div>
                      <div className="text-xs text-ink-muted dark:text-slate-400 truncate">
                        {a.absenceTypeName ?? '—'} · {formatDate(a.startDate)} – {formatDate(a.endDate)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
